import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip, Typography } from '@mui/material';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** COMPONENT */
import LabelRanking from 'component/LabelRanking';

/** UTILS */
import { checkPackage, getCrm, getMember, numberFormat } from 'utils/app.utils';

/** CONSTANTS */
import { PACKAGE_ADDONS } from 'constants/package';
import { colors } from 'constants/theme';

/** STYLE */
import InfoIcon from '@mui/icons-material/Info';
import { fileUrl } from 'api/api';
import { TreatmentStateInterface, treatment } from 'app/slice/treatment.slice';
import IconGiftVoucher from 'assets/icon/icon-gift-voucher';
import IconPromotion from 'assets/icon/icon-promotion';
import { imgs } from 'constants/images';
import _ from 'lodash';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Balance, ProfileAvatar } from './UseStyledFormPayment';



type InfoProps = {
  patient: any
  finance: { advanceBalance: number, outstandingBalance: number }
  treatmentRights?: any[]
}

function Info(props: InfoProps) {
  const { t } = useTranslation()
  const treatments: TreatmentStateInterface = useSelector(treatment)
  const [promotionUse, setPromotionUse] = useState<any>([])
  const [voucherUse, setVoucherUse] = useState<any>([])

  const memberAll = useMemo(() => getMember(), [])

  const renderImage = useCallback((img: any) => {
    if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName))
      return `${fileUrl}/${img.filePath}/${img.fileName}`
    return imgs.defaultAvatar
  }, [])

  const checkEvenByIndex = useCallback((index: number) => {
    return index % 2 === 0 ? 'justify-content-start' : 'justify-content-end'
  }, [])

  const { extractedPromotions, extractedVouchers } = useMemo(() => {
    // Dedupe by promotionBranchId but track how many items each promo is
    // applied to, so the UI can show "โปรโมชัน X (N)" when N > 1.
    const promotionMap = new Map<number, { info: any; count: number }>()
    const vouchers: any[] = []

    if (treatments?.summeryTreatments?.length) {
      treatments.summeryTreatments.forEach((item: any) => {
        if (item?.promotion?.length) {
          item.promotion.forEach((info: any) => {
            const branchId = info?.promotionBranchId
            if (branchId == null) return
            const existing = promotionMap.get(branchId)
            if (existing) existing.count += 1
            else promotionMap.set(branchId, { info, count: 1 })
          })
        }

        if (item?.voucherListId && item?.voucher) {
          vouchers.push(item.voucher)
        }
      })
    }

    const promotions = Array.from(promotionMap.values()).map(({ info, count }) => ({
      ...info,
      applicationCount: count,
    }))

    return { extractedPromotions: promotions, extractedVouchers: vouchers }
  }, [treatments?.summeryTreatments])

  useEffect(() => {
    setPromotionUse(extractedPromotions)
    setVoucherUse(extractedVouchers)
  }, [extractedPromotions, extractedVouchers])

  const rankColor = useMemo(() =>
    _.find(memberAll, { rankId: props.patient.rankId })?.color,
    [memberAll, props.patient.rankId]
  )

  const profileImage = useMemo(() =>
    renderImage(props.patient.profile),
    [renderImage, props.patient.profile]
  )

  return (
    <Box className='d-flex px-md-2 px-xl-3 pb-3 mb-3' sx={{ borderBottom: `1px solid ${colors.lightGray}` }}>
      <ProfileAvatar alt="profile" src={profileImage} sx={{ border: `3px solid ${getCrm() ? rankColor : colors.black12}`, backgroundColor: colors.white }}>
        <img src={imgs.defaultAvatar} alt="" className="w-100 h-100" />
      </ProfileAvatar>
      <Box className='ml-2 ml-sm-3 w-100 overflow-hidden'>
        <Box className="d-flex flex-column flex-sm-row align-items-sm-center">
          <Typography className="d-inline-block align-middle text-ellipsis" sx={{ fontSize: { sm: '18px' }, fontWeight: 600, color: colors.themeMainColor }}>
            {props.patient.branchCnNumber ? `${props.patient.branchCnNumber}` : ''} | {props.patient.firstname} {props.patient.lastname}
          </Typography>
          <Box className="d-inline-block ml-sm-2">
            {getCrm() && <LabelRanking rankId={props.patient.rankId} />}
          </Box>
        </Box>
        <Box className='pt-2'>
          {checkPackage(PACKAGE_ADDONS.ADDON_ADVANCE) && (
            <Box className='d-flex justify-content-between'>
              <Balance className='pr-1'>{t('PAYMENT.FORM_PAYMENT.ADVANCE_BALANCE')}</Balance>
              <Balance><span className="pr-sm-2">{numberFormat(props.finance.advanceBalance)}</span> {t('CURRENCY_CHANGE.TH')}</Balance>
            </Box>
          )}
          <Box className='d-flex justify-content-between'>
            <Balance className='pr-1' sx={{ color: colors.themeThirdColor }}>{t('PAYMENT.FORM_PAYMENT.OUT_STANDING_BALANCE')}</Balance>
            <Balance sx={{ color: colors.themeThirdColor }}><span className="pr-sm-2">{numberFormat(props.finance.outstandingBalance)}</span> {t('CURRENCY_CHANGE.TH')}</Balance>
          </Box>
          {props.treatmentRights?.length ? (
            <>
              <Box className='d-flex flex-wrap '>
                {_.map(props.treatmentRights, (treatmentRight, index) => {
                  return (
                    <div className={`${checkEvenByIndex(index)} w-50 d-flex`}>
                      <Typography >{treatmentRight.treatmentRightName}</Typography>
                      <Tooltip title={t('PAYMENT.FORM_PAYMENT.TREATMENT_DISCOUNT_INFO', {
                        price: treatmentRight.isDivide === '1' ? treatmentRight.balance : treatmentRight.price,
                        unit: treatmentRight.unit === 'BAHT' ? ` ${t('CURRENCY_CHANGE.TH')}` : '%',
                        balance: treatmentRight.isDivide === '1' ? 1 : treatmentRight.balance,
                        unitIsDivide: treatmentRight.isDivide === '1' ? `${t('PAYMENT.FORM_PAYMENT.RIGHT')}` : `${t('PAYMENT.FORM_PAYMENT.TIMES')}`,
                        isNoDF: treatmentRight.isDoctorFee === '1' ? '' : `${t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.NO')}`
                      })} placement="top">
                        <InfoOutlinedIcon className='ml-2' sx={{ fontSize: '16px', marginTop: '5px' }} />
                      </Tooltip>
                    </div>
                  )
                })}
              </Box>
            </>
          ) : (<></>)}
          {(promotionUse?.length) ? (
            <Box display={'flex'} flexDirection={'column'}>
              {promotionUse.map((item: any, index: number) => {
                return (
                  <Box key={index} display={'flex'} alignItems={'center'} gap={'8px'}>
                    <Box sx={{ 'path': { fill: '#A8A8A8' }, }}>
                      <IconPromotion />
                    </Box>
                    {`${item?.promotionName || ''}${item?.applicationCount > 1 ? ` (${item.applicationCount})` : ''}`}
                    {item?.detail &&
                      <Tooltip title={item?.detail || ''} placement={'top-start'} sx={{ 'path': { fill: '#A8A8A8' }, }}>
                        <Box>
                          <InfoIcon />
                        </Box>
                      </Tooltip>
                    }
                  </Box>
                )
              })

              }
            </Box>
          ) : <></>}
          {(voucherUse?.length) ? (
            <Box display={'flex'} flexDirection={'column'}>
              {voucherUse.map((item: any, index: number) => {
                return (
                  <Box key={index} display={'flex'} alignItems={'center'} gap={'8px'}>
                    <Box sx={{ 'path': { fill: '#A8A8A8' }, }}>
                      <IconGiftVoucher />
                    </Box>
                    {`${item?.voucherCode || ''} ${item?.voucherName}`}
                    {item?.detail &&
                      <Tooltip title={item?.detail || ''} placement={'top-start'} sx={{ 'path': { fill: '#A8A8A8' }, }}>
                        <Box>
                          <InfoIcon />
                        </Box>
                      </Tooltip>
                    }
                  </Box>
                )
              })

              }
            </Box>
          ) : <></>}
        </Box>
      </Box>
    </Box>
  )
}

export default memo(Info)
